import { Component, computed, inject } from "@angular/core";
import { ProjectService } from "../../../shared/services/project.service";
import { rxResource } from "@angular/core/rxjs-interop";
import { JobStateService } from "../../../projects/project/model-new/job-state.store";
import { NzSpinComponent } from "ng-zorro-antd/spin";
import { EChartsOption, LineSeriesOption } from "echarts/types/dist/echarts";
import { NgxEchartsModule } from "ngx-echarts";
import { HttpErrorResponse } from "@angular/common/http";
import { NzAlertComponent } from "ng-zorro-antd/alert";

interface AvailabilityEntry {
  timestamp: string;
  worker_count: number;
}
interface IterationDumpEntry {
  timestamp: string;
  iteration: number;
}
interface RunEntry { 
  workers: AvailabilityEntry[],
  iterations: IterationDumpEntry[]
}
type AvailabilityResponse = Record<string, RunEntry>;

// ECharts default palette as [r, g, b]; solid for lines, translucent for markAreas
const RUN_COLORS: [number, number, number][] = [
  [84, 112, 198],
  [145, 204, 117],
  [250, 200, 88],
  [238, 102, 102],
  [115, 192, 222],
  [59, 162, 114],
];

@Component({
  selector: "app-worker-availability-graph",
  imports: [NzSpinComponent, NgxEchartsModule, NzAlertComponent],
  templateUrl: "./worker-availability-graph.component.html",
  styleUrl: "./worker-availability-graph.component.less",
})
export class WorkerAvailabilityGraphComponent {
  projectService = inject(ProjectService);
  jobSerivce = inject(JobStateService);

  protected readonly workerAvailabilityResource = rxResource<
    AvailabilityResponse,
    string | undefined
  >({
    params: () => this.jobSerivce.jobData()?.id ?? undefined,
    stream: ({ params }) => {
      return this.projectService.getWorkerAvailability(params);
    },
  });

  protected readonly errorMessage = computed(() => 
    (this.workerAvailabilityResource.error()?.cause as HttpErrorResponse).error?.detail ?? "An unexpected Error occurred"
  )

  readonly chartOption = computed<EChartsOption>(() => {
    const rawData = this.workerAvailabilityResource.value();
    if (!rawData) return {};

    // Backend sends "YYYY-MM-DD,HH:mm:ss.SSS"; swap the comma for "T" to get ISO (parsed as local time)
    const toTime = (ts: string) => new Date(ts.replace(',', 'T')).getTime();

    // Sort each run's entries chronologically, then order runs by their first timestamp
    const runs = Object.entries(rawData)
      .filter(([, run]) => run?.workers?.length)
      .map(([runName, run]) => ({
        runName,
        entries: [...run.workers].sort((a, b) => toTime(a.timestamp) - toTime(b.timestamp)),
        iterations: [...(run.iterations ?? [])].sort(
          (a, b) => toTime(a.timestamp) - toTime(b.timestamp)
        ),
      }))
      .sort((a, b) => toTime(a.entries[0].timestamp) - toTime(b.entries[0].timestamp));

    // One series per run so discrete runs are never joined across the gaps between them
    const series: LineSeriesOption[] = runs.map(({ runName, entries, iterations }, idx) => {
      const [r, g, b] = RUN_COLORS[idx % RUN_COLORS.length];
      const startTime = toTime(entries[0].timestamp);
      // Pad single-sample runs so their markArea isn't zero-width
      const endTime = Math.max(toTime(entries[entries.length - 1].timestamp), startTime + 60_000);

      return {
        name: runName,
        type: 'line',
        step: 'end',
        smooth: false,
        showSymbol: false,
        data: entries.map((e) => [toTime(e.timestamp), e.worker_count]),
        itemStyle: { color: `rgb(${r}, ${g}, ${b})` },
        lineStyle: { width: 2.5 },
        // Drop overlapping iteration labels when dumps are dense (the lines stay)
        labelLayout: { hideOverlap: true },
        // One dotted line per iteration dump, in the run's colour
        markLine: {
          symbol: 'none',
          lineStyle: { type: 'dotted', width: 1, color: `rgba(${r}, ${g}, ${b}, 0.7)` },
          label: {
            position: 'insideEndTop',
            fontSize: 10,
            color: `rgb(${r}, ${g}, ${b})`,
          },
          data: iterations.map((it) => ({
            name: `Iteration ${it.iteration}`,
            xAxis: toTime(it.timestamp),
            label: { formatter: String(it.iteration) },
          })),
        },
        markArea: {
          silent: true,
          label: {
            show: true,
            position: 'top',
            color: '#444',
            fontSize: 12,
            fontWeight: 'bold',
          },
          data: [
            [
              {
                name: runName,
                xAxis: startTime,
                itemStyle: { color: `rgba(${r}, ${g}, ${b}, 0.15)` },
              },
              { xAxis: endTime },
            ],
          ],
        },
      };
    });

    // Local midnights spanned by the runs, used to mark day changes
    const midnights: number[] = [];
    if (runs.length) {
      const first = new Date(toTime(runs[0].entries[0].timestamp));
      const last = Math.max(...runs.map((r) => toTime(r.entries[r.entries.length - 1].timestamp)));
      for (
        let day = new Date(first.getFullYear(), first.getMonth(), first.getDate() + 1);
        day.getTime() <= last;
        day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)
      ) {
        midnights.push(day.getTime());
      }
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line' },
      },
      legend: { type: 'scroll', top: 0, data: runs.map((r) => r.runName) },
      grid: { left: '1%', right: '1%', top: 75, bottom: '12%', containLabel: true },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          hideOverlap: true,
          formatter: {
            year: '{yyyy}',
            month: '{MMM}',
            day: '{bold|{d} {MMM}}',
            hour: '{HH}:{mm}',
            minute: '{HH}:{mm}',
            second: '{HH}:{mm}:{ss}',
          },
          rich: { bold: { fontWeight: 'bold' } },
        },
      },
      yAxis: { type: 'value', name: 'Workers', minInterval: 1 },
      dataZoom: [{ type: 'inside' }, { type: 'slider' }],
      series,
    };
  });
}
